/**
 * Drilldown Modal Component for Supplier Performance
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PurchaseOrder, PurchaseOrderLine, GoodsReceiptNote, NonConformanceReport } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { ModalType } from './types';
import { formatCurrency } from './utils';
import { MODAL_PAGE_SIZE } from './constants';

interface DrilldownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  type: ModalType;
  data: unknown[];
  loading: boolean;
  page: number;
  totalCount: number;
  totalPages: number;
  currency: string;
  onPageChange: (page: number) => void;
}

export function DrilldownModal({
  open,
  onOpenChange,
  title,
  subtitle,
  type,
  data,
  loading,
  page,
  totalCount,
  totalPages,
  currency,
  onPageChange,
}: DrilldownModalProps) {
  const { formatDate } = useFormatters();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : data.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No data available</p>
          ) : type === 'po' ? (
            <POContent data={data as unknown as PurchaseOrder[]} currency={currency} formatDate={formatDate} />
          ) : type === 'ncr' ? (
            <NCRContent data={data as unknown as NonConformanceReport[]} currency={currency} formatDate={formatDate} />
          ) : type === 'grn' ? (
            <GRNContent data={data as unknown as GoodsReceiptNote[]} formatDate={formatDate} />
          ) : type === 'pol' ? (
            <POLineContent data={data as unknown as PurchaseOrderLine[]} currency={currency} formatDate={formatDate} />
          ) : null}
        </div>

        {/* Pagination footer */}
        {!loading && data.length > 0 && (
          <div className="pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Showing {(page - 1) * MODAL_PAGE_SIZE + 1} -{' '}
              {Math.min(page * MODAL_PAGE_SIZE, totalCount || data.length)} of{' '}
              {totalCount || data.length} record{(totalCount || data.length) !== 1 ? 's' : ''}
            </div>
            {(totalCount > MODAL_PAGE_SIZE || data.length === MODAL_PAGE_SIZE) && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="px-2">
                  Page {page} of {totalPages || '?'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Purchase Orders content
function POContent({ data, currency, formatDate }: { data: PurchaseOrder[]; currency: string; formatDate: (d: string | Date) => string }) {
  return (
    <div className="space-y-2">
      {data.map((po) => (
        <div key={po.id} className="border rounded-lg p-4 hover:bg-muted/50">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-semibold">{po.po_number}</div>
              <div className="text-sm text-muted-foreground">
                Delivery: {formatDate(po.delivery_date)}
              </div>
            </div>
            <div className="text-right">
              <Badge>{po.status}</Badge>
              <div className="text-sm font-semibold mt-1">
                {formatCurrency(po.total_value, currency)}
              </div>
            </div>
          </div>
          {po.line_count && (
            <div className="text-xs text-muted-foreground mt-2">{po.line_count} line items</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Non-Conformance Reports content
function NCRContent({ data, currency, formatDate }: { data: NonConformanceReport[]; currency: string; formatDate: (d: string | Date) => string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="p-2 text-left font-semibold">NCR Number</th>
            <th className="p-2 text-left font-semibold">Part Number</th>
            <th className="p-2 text-left font-semibold">Description</th>
            <th className="p-2 text-center font-semibold">Severity</th>
            <th className="p-2 text-right font-semibold">Qty Affected</th>
            <th className="p-2 text-right font-semibold">Unit Price</th>
            <th className="p-2 text-left font-semibold">Date Raised</th>
            <th className="p-2 text-center font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {data.map((ncr) => (
            <tr key={ncr.id} className="border-b hover:bg-muted/30">
              <td className="p-2 font-medium">{ncr.ncr_number}</td>
              <td className="p-2">{ncr.part_number}</td>
              <td className="p-2 max-w-[200px] truncate" title={ncr.description}>
                {ncr.description}
              </td>
              <td className="p-2 text-center">
                <Badge
                  className={
                    ncr.severity === 'critical'
                      ? 'bg-red-100 text-red-800'
                      : ncr.severity === 'major'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                  }
                >
                  {ncr.severity}
                </Badge>
              </td>
              <td className="p-2 text-right">{ncr.quantity_affected}</td>
              <td className="p-2 text-right">{formatCurrency(ncr.unit_price, currency)}</td>
              <td className="p-2">{formatDate(ncr.ncr_date)}</td>
              <td className="p-2 text-center">
                <Badge variant="outline">{ncr.status_display || ncr.status}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Goods Receipt Notes content
function GRNContent({ data, formatDate }: { data: GoodsReceiptNote[]; formatDate: (d: string | Date) => string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="p-2 text-left font-semibold">GRN Number</th>
            <th className="p-2 text-left font-semibold">PO Number</th>
            <th className="p-2 text-left font-semibold">Line #</th>
            <th className="p-2 text-left font-semibold">Part Number</th>
            <th className="p-2 text-left font-semibold">Description</th>
            <th className="p-2 text-right font-semibold">Qty Received</th>
            <th className="p-2 text-right font-semibold">Qty Accepted</th>
            <th className="p-2 text-right font-semibold">Qty Rejected</th>
            <th className="p-2 text-left font-semibold">Receipt Date</th>
            <th className="p-2 text-center font-semibold">Inspection</th>
          </tr>
        </thead>
        <tbody>
          {data.map((grn) => (
            <tr key={grn.id} className="border-b hover:bg-muted/30">
              <td className="p-2 font-medium">{grn.grn_number}</td>
              <td className="p-2">{grn.po_line_details?.po_number || '-'}</td>
              <td className="p-2">{grn.po_line_details?.line_number || '-'}</td>
              <td className="p-2">{grn.po_line_details?.part_number || '-'}</td>
              <td className="p-2 max-w-[200px] truncate" title={grn.po_line_details?.description}>
                {grn.po_line_details?.description || '-'}
              </td>
              <td className="p-2 text-right">{grn.quantity_received}</td>
              <td className="p-2 text-right text-green-600">{grn.quantity_accepted}</td>
              <td className="p-2 text-right text-red-600">{grn.quantity_rejected}</td>
              <td className="p-2">{formatDate(grn.receipt_date)}</td>
              <td className="p-2 text-center">
                <Badge
                  className={
                    grn.inspection_status === 'passed' || grn.inspection_status === 'received'
                      ? 'bg-green-100 text-green-800'
                      : grn.inspection_status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : grn.inspection_status === 'conditional'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                  }
                >
                  {grn.inspection_status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Purchase Order Lines content
function POLineContent({ data, currency, formatDate }: { data: PurchaseOrderLine[]; currency: string; formatDate: (d: string | Date) => string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1200px] text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="p-2 text-left font-semibold">PO Number</th>
            <th className="p-2 text-left font-semibold">Line #</th>
            <th className="p-2 text-left font-semibold">Part Number</th>
            <th className="p-2 text-left font-semibold">Description</th>
            <th className="p-2 text-right font-semibold">Qty Ordered</th>
            <th className="p-2 text-right font-semibold">Qty Received</th>
            <th className="p-2 text-right font-semibold">Outstanding</th>
            <th className="p-2 text-left font-semibold">Delivery Date</th>
            <th className="p-2 text-right font-semibold">Unit Price</th>
            <th className="p-2 text-center font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {data.map((line) => (
            <tr key={line.id} className="border-b hover:bg-muted/30">
              <td className="p-2 font-medium">{line.po_number || '-'}</td>
              <td className="p-2">{line.line_number}</td>
              <td className="p-2">{line.part_number}</td>
              <td className="p-2 max-w-[200px] truncate" title={line.description}>
                {line.description}
              </td>
              <td className="p-2 text-right">{line.quantity}</td>
              <td className="p-2 text-right text-green-600">{line.received_qty || '0'}</td>
              <td className="p-2 text-right text-orange-600">{line.outstanding_qty || '0'}</td>
              <td className="p-2">{formatDate(line.delivery_date)}</td>
              <td className="p-2 text-right">{formatCurrency(line.unit_price, currency)}</td>
              <td className="p-2 text-center">
                <Badge variant="outline">{line.status}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
